'use client';

import React from 'react';
import Link from 'next/link';
import { Check, Zap } from 'lucide-react';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Perfect for getting started',
    features: ['5 recordings/month', '720p quality', '3 AI clips/month', 'Basic analytics', '5GB storage'],
    cta: 'Get Started Free',
    href: '/register?plan=free',
    highlight: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$49',
    period: '/month',
    description: 'For serious content creators',
    features: ['Unlimited recordings', '4K quality', 'Unlimited AI clips', 'Advanced analytics', '500GB storage', 'Priority support', 'Custom branding'],
    cta: 'Start Pro Trial',
    href: '/register?plan=pro',
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$149',
    period: '/month',
    description: 'For teams and agencies',
    features: ['Everything in Pro', 'Unlimited team members', 'White-label solution', 'API access', 'Dedicated support', 'SLA guarantee', 'Custom integrations'],
    cta: 'Contact Sales',
    href: '/register?plan=enterprise',
    highlight: false,
  },
];

export default function PricingSection() {
  return (
    <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 xl:px-10">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-xs font-semibold text-primary mb-3 sm:mb-4">
            <Zap size={12} /> Simple Pricing
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3 sm:mb-4">
            Choose your plan
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            Start free, upgrade when you need more. No hidden fees, cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {PLANS?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative rounded-2xl p-5 sm:p-6 border transition-all duration-200 ${
                plan?.highlight
                  ? 'border-primary bg-primary/5 shadow-xl shadow-primary/10'
                  : 'border-border bg-card hover:border-border/80'
              }`}
            >
              {plan?.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-bold rounded-full whitespace-nowrap">
                  MOST POPULAR
                </div>
              )}
              <div className="mb-4 sm:mb-5">
                <h3 className="font-bold text-base sm:text-lg mb-1">{plan?.name}</h3>
                <p className="text-xs text-muted-foreground mb-2 sm:mb-3">{plan?.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl sm:text-3xl font-bold">{plan?.price}</span>
                  <span className="text-xs sm:text-sm text-muted-foreground">{plan?.period}</span>
                </div>
              </div>
              <ul className="space-y-2 mb-5 sm:mb-6">
                {plan?.features?.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs sm:text-sm">
                    <Check size={13} className="text-green-400 flex-shrink-0" />
                    <span className="text-muted-foreground">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                href={plan?.href}
                className={`block w-full text-center py-2.5 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95 touch-manipulation ${
                  plan?.highlight
                    ? 'bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20'
                    : 'bg-muted hover:bg-muted/80 text-foreground border border-border'
                }`}
              >
                {plan?.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}